import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertOctagon, Clock, ShieldAlert } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const publicClient = axios.create({ baseURL: API });

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 10; // ~20 seconds max

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || "";
  const [state, setState] = useState("polling"); // polling | paid | failed | expired
  const [detail, setDetail] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    document.title = "Payment Status — Silent Shield";
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const r = await publicClient.get(`/payments/v1/checkout/status/${sessionId}`);
        if (cancelled) return;
        setDetail(r.data);
        if (r.data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (r.data.status === "expired") {
          setState("expired");
          return;
        }
        if (attemptsRef.current >= MAX_POLLS) {
          setState("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        // Unknown / expired session id → server returns 404; transition out fast.
        if (err?.response?.status === 404) {
          setState("expired");
          return;
        }
        if (attemptsRef.current >= MAX_POLLS) setState("failed");
        else setTimeout(poll, POLL_INTERVAL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="checkout-success-page">
      <header className="bg-zinc-950 text-white">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80">
            <div className="w-7 h-7 border-2 border-white grid place-items-center">
              <ShieldAlert className="w-3.5 h-3.5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-400">Silent Shield</div>
              <div className="font-display font-black tracking-tight text-base">ADA INTERCEPTOR</div>
            </div>
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-16">
        {state === "polling" && (
          <div className="bg-white border-2 border-zinc-950 p-10 text-center" data-testid="checkout-state-polling">
            <Loader2 className="w-12 h-12 text-blue-700 mx-auto animate-spin" />
            <h1 className="mt-5 font-display text-3xl font-black tracking-tight">
              Confirming payment…
            </h1>
            <p className="mt-3 text-zinc-700">
              Your card processed successfully with Stripe. We&apos;re finalizing the transaction on our side — this usually takes a few seconds.
            </p>
            <p className="mt-2 text-xs text-zinc-500 font-mono">
              <Clock className="w-3 h-3 inline mr-1" /> Polling… attempt {attemptsRef.current}
            </p>
          </div>
        )}

        {state === "paid" && (
          <div
            className="bg-white border-2 border-emerald-600 p-10 text-center shadow-[10px_10px_0_0_rgba(5,150,105,1)]"
            data-testid="checkout-state-paid"
          >
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto" />
            <h1 className="mt-5 font-display text-3xl sm:text-4xl font-black tracking-tight">
              You&apos;re protected.
            </h1>
            <p className="mt-3 text-zinc-700 text-base leading-relaxed">
              Payment confirmed. Welcome to <span className="font-bold">Silent Shield</span>.
              Our team will reach out within one business day with your deployment kit and onboarding call link.
            </p>
            {detail?.metadata?.package_label && (
              <div className="mt-5 inline-block bg-zinc-950 text-white px-4 py-2 text-xs font-bold uppercase tracking-wider">
                {detail.metadata.package_label}
                {detail.amount_total != null && (
                  <span className="ml-2 font-mono">${(detail.amount_total / 100).toFixed(2)} {detail.currency?.toUpperCase()}</span>
                )}
              </div>
            )}
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="bg-zinc-950 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider text-sm px-5 py-3">
                Back to Home
              </Link>
              <a href="mailto:support@adainterceptor.com" className="bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider text-sm px-5 py-3">
                Email support
              </a>
            </div>
          </div>
        )}

        {state === "expired" && (
          <div className="bg-white border-2 border-amber-600 p-10 text-center" data-testid="checkout-state-expired">
            <AlertOctagon className="w-14 h-14 text-amber-600 mx-auto" />
            <h1 className="mt-5 font-display text-3xl font-black">Session expired</h1>
            <p className="mt-3 text-zinc-700">
              The checkout session expired before payment completed. Pick a plan again to retry.
            </p>
            <Link to="/" className="mt-6 inline-block bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider text-sm px-5 py-3">
              Back to Pricing
            </Link>
          </div>
        )}

        {state === "failed" && (
          <div className="bg-white border-2 border-red-600 p-10 text-center" data-testid="checkout-state-failed">
            <AlertOctagon className="w-14 h-14 text-red-600 mx-auto" />
            <h1 className="mt-5 font-display text-3xl font-black">We couldn&apos;t confirm payment</h1>
            <p className="mt-3 text-zinc-700">
              {sessionId ? "We're still waiting for Stripe to confirm. If you see a charge on your card, email support and we&apos;ll reconcile manually." : "No checkout session id was provided."}
            </p>
            <p className="mt-2 text-xs text-zinc-500 font-mono">{sessionId || "(no session id)"}</p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="bg-zinc-950 hover:bg-zinc-800 text-white font-bold uppercase tracking-wider text-sm px-5 py-3">
                Back to Home
              </Link>
              <a href="mailto:support@adainterceptor.com" className="bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wider text-sm px-5 py-3">
                Email Support
              </a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
